import React, { useState } from 'react';
import { IMPROVEMENT_STATUS, QUALITY_STATUS } from '../constants/dataQuality';

// The guide is tab-scoped: it explains only the view you're looking at. Each tab draws
// composition bars, but with its own band labels — Quality splits by provenance (ATS vs
// SignalCue), History splits by run (carried vs added-this-run).
const QUALITY_BANDS = [
  { c: '#eda100', label: 'From your ATS', desc: 'The value your export already carried.' },
  { c: '#1baf7a', label: 'Added by SignalCue', desc: 'We derived it from your other fields, or discovered it externally.' },
  { c: '#d6d5cf', label: 'Still missing', desc: 'Empty — can still be filled by a future enrich run.', border: true },
  { c: '#e79797', label: "Can't enrich", desc: 'We searched and found nothing verifiable — a real dead-end, not a gap to retry.' }
];

const HISTORY_BANDS = [
  { c: '#4f6d99', label: 'Before this run', desc: 'Already filled before the run you’re viewing — whether your ATS carried it or an earlier enrich run added it. (A timing split, so it won’t line up with the Quality tab’s ATS-source band, which counts only what your export brought in.)' },
  { c: '#1baf7a', label: 'Added this run', desc: 'Values this run filled — the run’s contribution.' },
  { c: '#d6d5cf', label: 'Still missing', desc: 'Empty — can still be filled by a later run.', border: true },
  { c: '#e79797', label: "Can't enrich", desc: 'Searched, nothing verifiable found — a real dead-end.' }
];

function BandList({ bands }) {
  return (
    <ul className="space-y-2.5">
      {bands.map((b) => (
        <li key={b.label} className="flex items-start gap-2.5">
          <span
            className="inline-block w-3 h-3 rounded-sm flex-none mt-0.5"
            style={{ background: b.c, border: b.border ? '1px solid #c3c2b7' : 'none' }}
          />
          <span className="leading-relaxed">
            <span className="font-medium text-gray-800">{b.label}</span> — {b.desc}
          </span>
        </li>
      ))}
    </ul>
  );
}

function TermList({ title, items }) {
  return (
    <section>
      <h4 className="text-sm font-semibold text-gray-900 mb-3">{title}</h4>
      <dl className="space-y-3">
        {items.map(([key, meta]) => (
          <div key={key}>
            <dt className="text-sm font-medium text-gray-800">{meta.label}</dt>
            <dd className="text-sm text-gray-600 mt-0.5 leading-relaxed">{meta.description}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function QualityGuide() {
  return (
    <div className="text-sm text-gray-600">
      <p className="pt-4 leading-relaxed max-w-3xl">
        The Quality view shows how complete your records are <strong className="font-medium text-gray-800">right now</strong> —
        and where each value came from. Every bar counts individual{' '}
        <strong className="font-medium text-gray-800">values</strong> — one per record, per field — not records.
      </p>

      <section className="mt-5 p-4 bg-white rounded-lg border border-slate-200">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">The four bands in every bar</h4>
        <BandList bands={QUALITY_BANDS} />
        <p className="text-xs text-gray-500 mt-3 leading-relaxed">
          The first two bands together are your <strong className="font-medium text-gray-700">completeness</strong> — the
          share of values with a usable answer. The last two split the remaining gap into what can still be filled versus
          what we&apos;ve already exhausted.
        </p>
      </section>

      <section className="mt-5 p-4 bg-white rounded-lg border border-slate-200">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">The stat cards &amp; the change figure</h4>
        <p className="leading-relaxed">
          The cards at the top total those bands across all your records. Next to a percentage, the coloured figure (e.g.{' '}
          <span className="text-green-700 font-medium">▲ 2.9 pp</span>) is the change in completeness in percentage points —{' '}
          <span className="text-green-700 font-medium">green</span> when more values are now filled,{' '}
          <span className="text-red-700 font-medium">red</span> if fewer. Formats aren&apos;t validated yet, so nothing is
          flagged &ldquo;invalid&rdquo; — health is rated on completeness alone.
        </p>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
        <TermList title="Record-type health" items={Object.entries(QUALITY_STATUS)} />
        <TermList title="Field change" items={Object.entries(IMPROVEMENT_STATUS)} />
      </div>
    </div>
  );
}

function HistoryGuide() {
  return (
    <div className="text-sm text-gray-600">
      <p className="pt-4 leading-relaxed max-w-3xl">
        The History view shows how each enrichment run improved your data{' '}
        <strong className="font-medium text-gray-800">over time</strong>. Step to any run to see what it added. Every bar
        counts individual <strong className="font-medium text-gray-800">values</strong> — one per record, per field — not
        records.
      </p>

      <section className="mt-5 p-4 bg-white rounded-lg border border-slate-200">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">The four bands in every bar</h4>
        <BandList bands={HISTORY_BANDS} />
      </section>

      <section className="mt-5 p-4 bg-white rounded-lg border border-slate-200">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Stepping through runs</h4>
        <ul className="space-y-2 leading-relaxed">
          <li>
            <span className="font-medium text-gray-800">Step between runs</span> with the ◀ ▶ arrows or the timeline dots
            to see what any single run added.
          </li>
          <li>
            <span className="font-medium text-gray-800">Compare to</span> switches the baseline — the same bars, only the
            before → after numbers change. <span className="font-medium">vs previous run</span> shows one run&apos;s
            contribution; <span className="font-medium">since ATS import</span> shows everything gained since your import.
          </li>
          <li>
            The coloured figure next to a percentage (e.g. <span className="text-green-700 font-medium">▲ 2.9 pp</span>) is
            that run&apos;s change in completeness, in percentage points.
          </li>
        </ul>
      </section>

      <section className="mt-5 p-4 bg-white rounded-lg border border-slate-200">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">The four run stats</h4>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 leading-relaxed">
          <li><span className="font-medium text-gray-800">Completeness</span> — the share of values filled, now.</li>
          <li><span className="font-medium text-gray-800">Added this run</span> — values this run filled.</li>
          <li><span className="font-medium text-gray-800">Can&apos;t enrich</span> — searched, evidence-backed dead-ends.</li>
          <li><span className="font-medium text-gray-800">Still fillable</span> — gaps worth another run.</li>
        </ul>
      </section>
    </div>
  );
}

const VIEW_LABEL = { quality: 'Quality', history: 'History' };

export default function DataQualityExplainabilityPanel({ tab = 'quality' }) {
  const [expanded, setExpanded] = useState(false);
  const isHistory = tab === 'history';

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-100/80 transition-colors"
        aria-expanded={expanded}
      >
        <h3 className="text-base font-semibold text-gray-900">How to read the {VIEW_LABEL[tab] || 'Quality'} view</h3>
        <span className="text-sm text-[#007A6B] font-medium shrink-0">{expanded ? 'Hide guide' : 'Show guide'}</span>
      </button>

      {expanded ? (
        <div className="px-5 pb-5 border-t border-slate-200">{isHistory ? <HistoryGuide /> : <QualityGuide />}</div>
      ) : null}
    </div>
  );
}
